import { FgNdc } from 'Interfaces/ReferenceInterfaces/StandardAndRecipeBpmInterface';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import classes from './addRowModalBpmByNdcAndJobType.module.scss';

interface FormData {
  roomTypeId: number;
  ndc: string;
  jobTypeId: number;
  description: string;
  bpm: number;
  moHc: number;
  paHc: number;
}

interface DropdownOption {
  label: string;
  value: number;
}

interface NdcDropdownOption {
  label: string;
  value: string;
  drugName: string;
}

interface AddRowModalBpmByNdcAndJobTypeProps {
  visible: boolean;
  onHide: () => void;
  onSubmit: (formData: FormData) => Promise<void>;
  initialValues?: FormData;
  isEdit?: boolean;
  roomOptions: DropdownOption[];
  jobTypeOptions: DropdownOption[];
  fgNDC: FgNdc[];
}

const AddRowModalBpmByNdcAndJobType: React.FC<AddRowModalBpmByNdcAndJobTypeProps> = ({
  visible,
  onHide,
  onSubmit,
  initialValues,
  isEdit = false,
  roomOptions,
  jobTypeOptions,
  fgNDC
}) => {
  const [formData, setFormData] = useState<FormData>({
    roomTypeId: 0,
    ndc: '',
    jobTypeId: 0,
    description: '',
    bpm: 0,
    moHc: 0,
    paHc: 0
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const environment = import.meta.env.VITE_ENVIRONMENT;

  // Convert fgNDC to dropdown options
  const ndcOptions: NdcDropdownOption[] = fgNDC.map(item => ({
    label: item.fg_ndc,
    value: item.fg_ndc,
    drugName: item.drug_name
  }));

  useEffect(() => {
    if (initialValues && visible) {
      setFormData({ ...initialValues });
      setErrors({});
    } else if (!isEdit && visible) {
      // Reset form for new entry
      setFormData({
        roomTypeId: 0,
        ndc: '',
        jobTypeId: 0,
        description: '',
        bpm: 0,
        moHc: 0,
        paHc: 0
      });
      setErrors({});
    }
  }, [initialValues, visible, isEdit]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Always validate required fields
    if (!formData.ndc) {
      newErrors.ndc = 'NDC is required';
    }
    if (!formData.description) {
      newErrors.description = 'Description is required';
    }
    if (!formData.roomTypeId) {
      newErrors.roomTypeId = 'Room Type is required';
    }
    if (!formData.jobTypeId) {
      newErrors.jobTypeId = 'Job Type is required';
    }

    // Validate numeric fields > 0
    if (formData.bpm <= 0) {
      newErrors.bpm = 'BPM must be greater than 0';
    }
    if (formData.moHc <= 0) {
      newErrors.moHc = 'MO HC must be greater than 0';
    }
    if (formData.paHc <= 0) {
      newErrors.paHc = 'PA HC must be greater than 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (validateForm()) {
      try {
        setIsSubmitting(true);
        await onSubmit(formData);
        // Only close modal on success
        onHide();
      } catch (error) {
        // Don't close modal on error - error will be shown in toast
        if (environment === 'development') {
          console.error('Error submitting form:', error);
        }
        toast.error('Error submitting form for BPM by NDC and Job Type');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleInputChange = (field: keyof FormData, value: string | number) => {
    // Add number-only validation for BPM, MO HC, and PA HC fields
    if ((field === 'bpm' || field === 'moHc' || field === 'paHc') && value !== '') {
      // Allow numbers and decimal points
      if (!/^\d*\.?\d*$/.test(value.toString())) {
        return; // Don't update if not a valid number
      }
    }

    setFormData({ ...formData, [field]: value });

    // Clear error when field is updated
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handleNdcChange = (selectedNdc: string) => {
    // Find the selected NDC option to get the drug name
    const selectedOption = ndcOptions.find(option => option.value === selectedNdc);

    setFormData({
      ...formData,
      ndc: selectedNdc,
      description: selectedOption ? selectedOption.drugName : ''
    });

    // Clear errors for both fields
    if (errors.ndc) {
      setErrors({ ...errors, ndc: '' });
    }
    if (errors.description) {
      setErrors({ ...errors, description: '' });
    }
  };

  return (
    <Dialog
      header={isEdit ? 'Edit BPM by NDC and Job Type' : 'Add New BPM by NDC and Job Type'}
      visible={visible}
      onHide={onHide}
      className={classes.modal}
      style={{ width: '700px' }}
    >
      <div className={classes.formContainer}>
        <div className={classes.formRow}>
          <div className={classes.formGroup}>
            <label>NDC</label>
            <Dropdown
              value={formData.ndc}
              options={ndcOptions}
              onChange={e => handleNdcChange(e.value)}
              placeholder="Select NDC"
              className={`${classes.dropdown} ${errors.ndc ? 'p-invalid' : ''}`}
              filter
              disabled={isSubmitting}
            />
            {errors.ndc && <small className="p-error">{errors.ndc}</small>}
          </div>

          <div className={classes.formGroup}>
            <label>Description</label>
            <InputText
              value={formData.description}
              onChange={e => handleInputChange('description', e.target.value)}
              placeholder="Drug name will be auto-populated"
              className={`${classes.inputText} ${errors.description ? 'p-invalid' : ''}`}
              readOnly={true}
            />
            {errors.description && <small className="p-error">{errors.description}</small>}
          </div>
        </div>

        <div className={classes.formRow}>
          <div className={classes.formGroup}>
            <label>Job Type</label>
            <Dropdown
              value={formData.jobTypeId}
              options={jobTypeOptions}
              onChange={e => handleInputChange('jobTypeId', e.value)}
              placeholder="Select Job Type"
              className={`${classes.dropdown} ${errors.jobTypeId ? 'p-invalid' : ''}`}
              filter
              disabled={isSubmitting}
            />
            {errors.jobTypeId && <small className="p-error">{errors.jobTypeId}</small>}
          </div>

          <div className={classes.formGroup}>
            <label>Room Type</label>
            <Dropdown
              value={formData.roomTypeId}
              options={roomOptions}
              onChange={e => handleInputChange('roomTypeId', e.value)}
              placeholder="Select Room Type"
              className={`${classes.dropdown} ${errors.roomTypeId ? 'p-invalid' : ''}`}
              filter
              disabled={isSubmitting}
            />
            {errors.roomTypeId && <small className="p-error">{errors.roomTypeId}</small>}
          </div>
        </div>

        <div className={classes.formRow}>
          <div className={classes.formGroup}>
            <label>BPM</label>
            <InputText
              value={formData.bpm.toString()}
              onChange={e => handleInputChange('bpm', parseFloat(e.target.value || '0'))}
              placeholder="Enter BPM"
              className={`${classes.inputText} ${errors.bpm ? 'p-invalid' : ''}`}
              disabled={isSubmitting}
            />
            {errors.bpm && <small className="p-error">{errors.bpm}</small>}
          </div>

          <div className={classes.formGroup}>
            <label>MO HC</label>
            <InputText
              value={formData.moHc.toString()}
              onChange={e => handleInputChange('moHc', e.target.value)}
              placeholder="Enter MO HC"
              className={`${classes.inputText} ${errors.moHc ? 'p-invalid' : ''}`}
              disabled={isSubmitting}
            />
            {errors.moHc && <small className="p-error">{errors.moHc}</small>}
          </div>
        </div>

        <div className={classes.formRow}>
          <div className={classes.formGroup}>
            <label>PA HC</label>
            <InputText
              value={formData.paHc.toString()}
              onChange={e => handleInputChange('paHc', e.target.value)}
              placeholder="Enter PA HC"
              className={`${classes.inputText} ${errors.paHc ? 'p-invalid' : ''}`}
              disabled={isSubmitting}
            />
            {errors.paHc && <small className="p-error">{errors.paHc}</small>}
          </div>
        </div>
      </div>

      <div className={classes.modalFooter}>
        <Button
          label="Cancel"
          icon="pi pi-times"
          onClick={onHide}
          severity="danger"
          text
          disabled={isSubmitting}
        />

        <Button
          label={isSubmitting ? 'Saving...' : 'Save'}
          icon={isSubmitting ? 'pi pi-spinner pi-spin' : 'pi pi-check'}
          onClick={handleSubmit}
          className={classes.saveButton}
          disabled={isSubmitting}
        />
      </div>
    </Dialog>
  );
};

export default AddRowModalBpmByNdcAndJobType;
